import {
  ComponentInternalInstance,
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  reactive,
} from '@vue/composition-api';
import async from 'async';
import { UploadFile } from '@/renderer/components/SpacePage/compositions/uploadHandler';

import TaskManager from '@/renderer/scripts/utils/TaskManager/BaseTaskManager';
import { FileInfo, TeamPathInfo } from '@/renderer/scripts/utils/upload/types';
import { handleUploadFilesToTask, uploadOptions } from '@/renderer/scripts/utils/upload';
import { downloadOptions } from '@/renderer/scripts/utils/download';
import { filesClassifyAdapter } from '@/renderer/scripts/utils/transmission/adapter/FileAdapter';
import { Task, TaskManagerList } from '@/renderer/scripts/utils/TaskManager/types';
import { DownloadFileInfo } from '@/renderer/scripts/utils/download/types';
import { IS_BROWSER } from '@/common/constant/platform';

let fs: typeof import('fs');
let path: typeof import('path');
let electron: typeof import('electron');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
  electron = (window.parent as any).require('electron') as typeof import('electron');
}

/**
 * 由于TaskManager是只有传输列表再使用的，和UI强绑定
 * 所以如果说要实现一个简单的文件上传功能，需要额外的使用一个新的 TaskManager 实例
 * TODO 目前暂时没有 download 的进度管理器
 * @constructor
 * @param options
 */
export const simpleTransmissionFileHandler = (options: { type: 'upload' | 'download' }) => {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;

  // 根据上传下载
  const taskOptions = options.type === 'upload' ? uploadOptions : downloadOptions;

  // 创建一个局部的任务管理器
  const taskManager = new TaskManager<FileInfo>(uploadOptions);

  const _downloadMethods = {};
  const _uploadMethods = {
    upload: async (files: UploadFile[], currPath: string, spaceId: string) => {
      const routerName = vm.$route.name || '';
      // 文件适配结构
      const res = await filesClassifyAdapter(files, currPath, spaceId, routerName);
      const [fileMap] = [res.fileMap];
      Object.values(fileMap).forEach(item => {
        item.forEach(item => {
          item.conflictResolutionStrategy = 'rename';
        });
      });
      await _uploadMethods.uploadFiles(fileMap);
    },
    uploadFiles: async (fileMap: Record<string, FileInfo[]>) => {
      // 加入上传列表中
      const tasks: Task<FileInfo>[] = await handleUploadFilesToTask(fileMap);
      async.forEachLimit(
        tasks,
        2,
        (task, next) => {
          taskManager.addTask(task);
          next();
        },
        () => {
          taskManager.start();
        },
      );
    },
  };

  // 上传管理器
  const uploadManagerList = reactive<TaskManagerList<FileInfo>>({
    running: [],
    waiting: [],
    finish: [],
    list: [],
  });
  const uploadList = computed<any>(
    () => [...uploadManagerList.running, ...uploadManagerList.waiting] as Task<FileInfo>[],
  );
  const upListTotal = computed<any>(() => [
    ...uploadManagerList.running,
    ...uploadManagerList.waiting,
    ...uploadManagerList.finish,
  ]);
  const finishList = computed<Task<FileInfo | DownloadFileInfo>[]>(() => {
    const uploadFinished: Task<FileInfo>[] = uploadManagerList.finish as Task<FileInfo>[];
    return uploadManagerList.finish;
  });
  const transmissionMethods = {
    updateUploadList(params: TaskManagerList<FileInfo>) {
      uploadManagerList.running = params.running;
      uploadManagerList.waiting = params.waiting;
      uploadManagerList.finish = params.finish;
    },
    /**
     * @description 处理传输列表操作
     * @param {string} command
     * @param {Task<FileInfo>} task
     */
    async handleTransmissionOperate(
      command: string,
      task: Task<FileInfo | DownloadFileInfo>,
      transmissionType: string,
    ) {
      switch (command) {
        case 'pauseTask':
        case 'restartTask':
        case 'retryTask':
        case 'cancelTask':
          if (transmissionType === 'upload') {
            taskManager[command](task.taskId);
          }
          break;
        case 'pause':
        case 'restart':
        case 'cancel':
          taskManager[command]();
          break;
        case 'openFolder':
          // do nothing
          break;
        default:
          break;
      }
    },
    /**
     * @description 处理已完成列表的操作
     * @param {string} command
     * @param {Task<FileInfo>} task
     */
    handleFinishOperate(command: string, task: Task<FileInfo | DownloadFileInfo>) {
      switch (command) {
        case 'cancelTask':
        case 'restartTask':
          taskManager[command](task.taskId);
          break;
        case 'cancel':
          taskManager[command]('finish');
          break;
        // pc端打开已上传文件目录
        case 'openFolder':
          // do nothing
          break;
        default:
          break;
      }
    },
  };
  transmissionMethods.updateUploadList(taskManager.getList());
  taskManager.on('list-update', transmissionMethods.updateUploadList);
  onBeforeUnmount(() => {
    taskManager.off('list-update', transmissionMethods.updateUploadList);
  });
  return {
    taskManager,
    methods: _uploadMethods,
    handleTransmissionOperate: transmissionMethods.handleTransmissionOperate,
    uploadList,
    upListTotal,
    finishList,
  };
};
